import React, { useState, useEffect } from 'react';
import {
  Form, FormGroup, Label, Input, Button,
  Alert, Badge, Row, Col
} from 'reactstrap';
import { Calendar, Clock, Save, X } from 'lucide-react';
import API from '../utils/api';

const SourceSchedule = ({ source, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    enabled: false,
    type: 'interval',
    hours: 6,
    minutes: 0,
    time: '00:00',
    cron: '0 0 * * *',
    day_of_week: '*',
    hour: '0',
    minute: '0'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [jobInfo, setJobInfo] = useState(null);

  useEffect(() => {
    if (source.schedule_config) {
      setFormData({
        ...formData,
        ...source.schedule_config,
        enabled: source.schedule_enabled || false
      });
    }
  }, [source]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const response = await API.post(`/sources/${source.id}/schedule`, formData);
      setJobInfo(response.data.job);
      if (onSave) onSave();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const generateCronExpression = () => {
    if (formData.type === 'daily') {
      const [hour, minute] = formData.time.split(':');
      return `${minute} ${hour} * * *`;
    } else if (formData.type === 'weekly') {
      return `${formData.minute} ${formData.hour} * * ${formData.day_of_week}`;
    } else if (formData.type === 'interval') {
      if (formData.hours > 0) {
        return `0 */${formData.hours} * * *`;
      } else {
        return `*/${formData.minutes} * * * *`;
      }
    }
    return formData.cron;
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup check>
        <Label check className="text-white mb-3">
          <Input
            type="checkbox"
            name="enabled"
            checked={formData.enabled}
            onChange={handleChange}
          />
          Enable Automatic Import
        </Label>
      </FormGroup>

      {formData.enabled && (
        <>
          <FormGroup>
            <Label className="text-white">Schedule Type</Label>
            <Input
              type="select"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="bg-dark text-white"
            >
              <option value="interval">Fixed Interval</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="cron">Custom Cron</option>
            </Input>
          </FormGroup>

          {formData.type === 'interval' && (
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-white">Hours</Label>
                  <Input
                    type="number"
                    name="hours"
                    value={formData.hours}
                    onChange={handleChange}
                    min="0"
                    max="23"
                    className="bg-dark text-white"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-white">Minutes</Label>
                  <Input
                    type="number"
                    name="minutes"
                    value={formData.minutes}
                    onChange={handleChange}
                    min="0"
                    max="59"
                    className="bg-dark text-white"
                  />
                </FormGroup>
              </Col>
            </Row>
          )}

          {formData.type === 'daily' && (
            <FormGroup>
              <Label className="text-white">Time</Label>
              <Input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                className="bg-dark text-white"
              />
            </FormGroup>
          )}

          {formData.type === 'weekly' && (
            <>
              <FormGroup>
                <Label className="text-white">Day of Week</Label>
                <Input
                  type="select"
                  name="day_of_week"
                  value={formData.day_of_week}
                  onChange={handleChange}
                  className="bg-dark text-white"
                >
                  <option value="0">Sunday</option>
                  <option value="1">Monday</option>
                  <option value="2">Tuesday</option>
                  <option value="3">Wednesday</option>
                  <option value="4">Thursday</option>
                  <option value="5">Friday</option>
                  <option value="6">Saturday</option>
                </Input>
              </FormGroup>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label className="text-white">Hour</Label>
                    <Input
                      type="number"
                      name="hour"
                      value={formData.hour}
                      onChange={handleChange}
                      min="0"
                      max="23"
                      className="bg-dark text-white"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="text-white">Minute</Label>
                    <Input
                      type="number"
                      name="minute"
                      value={formData.minute}
                      onChange={handleChange}
                      min="0"
                      max="59"
                      className="bg-dark text-white"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}

          {formData.type === 'cron' && (
            <FormGroup>
              <Label className="text-white">Cron Expression</Label>
              <Input
                type="text"
                name="cron"
                value={formData.cron}
                onChange={handleChange}
                placeholder="0 0 * * *"
                className="bg-dark text-white font-monospace"
              />
              <small className="text-muted">Format: minute hour day month day-of-week</small>
            </FormGroup>
          )}

          <Alert color="info" className="mt-3">
            <strong>Schedule Expression:</strong>
            <code className="ms-2">{generateCronExpression()}</code>
          </Alert>
        </>
      )}

      {jobInfo && (
        <Alert color="success">
          <strong>Job Scheduled!</strong><br />
          Next run: {jobInfo.next_run_time ? new Date(jobInfo.next_run_time).toLocaleString() : 'Not scheduled'}
        </Alert>
      )}

      {error && (
        <Alert color="danger">
          {error}
        </Alert>
      )}

      <div className="mt-3">
        <Button color="primary" type="submit" disabled={saving}>
          <Save size={16} className="me-1" />
          {saving ? 'Saving...' : 'Save Schedule'}
        </Button>
        <Button color="secondary" className="ms-2" onClick={onClose}>
          <X size={16} className="me-1" />
          Close
        </Button>
      </div>
    </Form>
  );
};

export default SourceSchedule;